import React, { useState, useEffect, useRef } from 'react';
import { AlertCircle, Send, Paperclip, Plus, X } from 'lucide-react';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import './chatinterface.css';
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000'; // Match your backend port

const ChatInterface = ({ userId, projectId, chatType, recipientId  }) => {
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [chatRooms, setChatRooms] = useState([]);
  const [activeChatRoom, setActiveChatRoom] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    fetchChatRooms();
  }, [userId, projectId, recipientId]);

  useEffect(() => {
    if (activeChatRoom) {
      fetchMessages();
      scrollToBottom();
      localStorage.setItem(`lastRead_${activeChatRoom}`, new Date().toISOString());
    }
  }, [activeChatRoom]);
  
  useEffect(() => {
    if (recipientId && chatType === 'direct') {
      // Create or get direct chat room when recipient changes
      createOrGetDirectChat();
    } else {
      fetchChatRooms();
    }
  }, [userId, recipientId, chatType]); 

  const createOrGetDirectChat = async () => {
    try {
      setLoading(true);
      console.log('Creating/getting direct chat for:', userId, 'with:', recipientId);
      
      // Use the API endpoint directly instead of trying to access the database
      const response = await fetch(`${API_URL}/api/chat/direct`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId: userId,
          recipientId: recipientId
        }),
      });
      
      if (!response.ok) {
        throw new Error(`Failed to initialize chat: ${response.status}`);
      }
      
      const data = await response.json();
      
      if (data.chat_room_id) {
        setActiveChatRoom(data.chat_room_id);
        await fetchChatRooms();
      } else {
        throw new Error('No chat room ID received');
      }
    } catch (error) {
      console.error('Error creating/getting direct chat:', error);
      setError(error.message || "Failed to initialize chat");
    } finally {
      setLoading(false);
    }
  };
  
  const fetchChatRooms = async () => {
    try {
      console.log('Fetching rooms for user:', userId, 'type:', chatType);
      const response = await fetch(`${API_URL}/api/chat/rooms?userId=${userId}&chatType=${chatType}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      console.log('Received chat rooms:', data);
      setChatRooms(data);
      
      // Only set active chat room if it's not already set or if there's no recipientId
      if (!activeChatRoom || !recipientId) {
        if (data.length > 0) {
          setActiveChatRoom(data[0].chat_room_id);
        }
      }
    } catch (err) {
      console.error('Error fetching chat rooms:', err);
      setError("Failed to load chat rooms: " + err.message);
    } finally {
      setLoading(false);
    }
  };
  
  const fetchMessages = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/api/chat/messages/${activeChatRoom}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      setMessages(data);
      setError(null); // Clear any previous errors
      
      // Mark messages as read
      localStorage.setItem(`lastRead_${activeChatRoom}`, new Date().toISOString());
    } catch (err) {
      console.error('Error fetching messages:', err);
      setError("Failed to load messages: " + err.message);
    } finally {
      setLoading(false);
    }
  };
    
  const sendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim()) return;
  
    try {
      const messageData = {
        chatRoomId: activeChatRoom,
        senderId: userId,
        content: newMessage
      };
      
      console.log('Sending message:', messageData);
      
      const response = await fetch(`${API_URL}/api/chat/send`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(messageData),
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to send message');
      }
      
      // Optimistically add message to UI
      const tempMessage = {
        message_id: 'temp-' + Date.now(),
        content: newMessage,
        sent_at: new Date().toISOString(),
        sender_id: userId,
        sender_name: 'You' // Will be replaced when fetchMessages runs
      };
      
      setMessages(prev => [...prev, tempMessage]);
      setNewMessage('');
      
      // Fetch the latest messages to update the UI
      setTimeout(fetchMessages, 300);
    } catch (err) {
      console.error('Send message error:', err);
      setError("Failed to send message: " + err.message);
    }
  };
  
  const closeChat = async (chatRoomId) => {
    try {
      const response = await fetch(`${API_URL}/api/chat/close`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          chatRoomId,
          userId,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to close chat');
      }

      // Remove the closed chat room from the list
      setChatRooms(prevRooms => prevRooms.filter(room => room.chat_room_id !== chatRoomId));
      
      // If this was the active chat room, set a new active room
      if (activeChatRoom === chatRoomId) {
        const remainingRooms = chatRooms.filter(room => room.chat_room_id !== chatRoomId);
        if (remainingRooms.length > 0) {
          setActiveChatRoom(remainingRooms[0].chat_room_id);
        } else {
          setActiveChatRoom(null);
        }
      }
    } catch (err) {
      console.error('Error closing chat:', err);
      setError('Failed to close chat: ' + err.message);
    }
  };

  if (loading && !chatRooms.length) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-xl text-gray-600">Loading chat rooms...</div>
      </div>
    );
  }
  
  if (error && !chatRooms.length) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-xl text-red-600">
          {error}
          <button 
            onClick={fetchChatRooms}
            className="mt-4 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }
  
  if (chatRooms.length === 0) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-xl text-gray-600">No chat rooms found</div>
      </div>
    );
  }

  return (
    <main className="flex h-screen bg-gray-50">
      {/* Sidebar with chat rooms */}
      <aside className="w-80 bg-white border-r border-gray-200 flex flex-col">
        <header className="p-4 border-b border-gray-200">
          <h1 className="text-lg font-semibold text-gray-900">
            {chatType === 'direct' ? 'Direct Messages' : 'Project Chat'}
          </h1>
        </header>

        <div className="flex-1 overflow-y-auto">
          {chatRooms.map((room) => (
            <div key={room.chat_room_id} className="relative group border-b border-gray-100">
              <button
                onClick={() => setActiveChatRoom(room.chat_room_id)}
                className={`w-full p-4 text-left hover:bg-gray-50 transition-colors ${
                  activeChatRoom === room.chat_room_id ? 'bg-blue-50' : ''
                }`}
              >
                <div className="flex justify-between items-center">
                  <h2 className="font-medium text-gray-900">
                    {room.name || "Unknown User"}
                  </h2>
                  {chatType === 'direct' && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        closeChat(room.chat_room_id);
                      }}
                      className="closebutton"
                      aria-label="Close chat"
                    >
                      <X className="h-4 w-4 text-gray-500" />
                    </button>
                  )}
                </div>
                <p className="text-sm text-gray-500 mt-1">
                  {room.last_message ? (
                    <span className="line-clamp-1">{room.last_message}</span>
                  ) : (
                    <span className="italic">No messages yet</span>
                  )}
                </p>
                <time className="text-xs text-gray-400 mt-1">
                  {new Date(room.last_activity).toLocaleDateString()}
                </time>
              </button>
            </div>
          ))}
        </div>
      </aside>

      {/* Main chat area */}
      <section className="flex-1 flex flex-col">
        {error && (
          <div className="p-4">
            <Alert severity="error">
              <AlertTitle>Error</AlertTitle>
              {error}
            </Alert>
          </div>
        )}

        {/* Messages area */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4" role="log">
          {messages.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              No messages yet. Start the conversation!
            </div>
          ) : (
            messages.map((message) => (
              <article
                key={message.message_id}
                className={`flex ${
                  message.sender_id === userId ? 'justify-end' : 'justify-start'
                }`}
              >
                <div
                  className={`max-w-sm rounded-lg p-3 ${
                    message.sender_id === userId
                      ? 'bg-blue-500 text-white ml-12'
                      : 'bg-gray-100 text-gray-900 mr-12'
                  }`}
                >
                  <header className="text-sm font-medium mb-1">
                    {message.sender_name || "Unknown User"}
                  </header>
                  <p className="break-words">{message.content}</p>
                  <footer className="text-xs mt-1 opacity-75">
                    <time dateTime={message.sent_at}>
                      {new Date(message.sent_at).toLocaleTimeString()}
                    </time>
                  </footer>
                </div>
              </article>
            ))
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Message input */}
        <footer className="border-t border-gray-200 bg-white p-4">
          <form onSubmit={sendMessage} className="flex items-center gap-2">
            <div className="flex items-center gap-2">
              <button
                type="button"
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                aria-label="Add attachment"
              >
                <Plus className="h-5 w-5 text-gray-500" />
              </button>
              <button
                type="button"
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                aria-label="Add file"
              >
                <Paperclip className="h-5 w-5 text-gray-500" />
              </button>
            </div>
            
            <input
              type="text"
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              placeholder="Type a message..."
              className="flex-1 p-2 border border-gray-300 rounded-md focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
              aria-label="Message input"
            />
            
            <button
              type="submit"
              disabled={!newMessage.trim()}
              className={`p-2 rounded-full text-white transition-colors ${
                newMessage.trim()
                  ? 'bg-blue-500 hover:bg-blue-600'
                  : 'bg-gray-300 cursor-not-allowed'
              }`}
              aria-label="Send message"
            >
              <Send className="h-5 w-5" />
            </button>
          </form>
        </footer>
      </section>
    </main>
  );
};

export default ChatInterface;